// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {
  SohoAlertDirective,
  SohoBusyIndicatorDirective,
  SohoLookupComponent,
  SohoModalDialogRef,
  SohoModalDialogService
} from 'ids-enterprise-ng';
import { AppConstants, FormRequestType, LMDialogButtonKey, LMUniqueIdService } from '@utils/index';
import { LMLookupDialogComponent } from './lm-lookup-dialog.component';
import { catchError, first } from 'rxjs/operators';
import { LMContextService, LMErrorUtilService, LMFormatUtilService, LMLoggerService, LMSpecService } from '@services/index';
import { Observable, of, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LMDimension } from '@modal/cubes/lm-cubes';
import { LMFieldValueChangedEvent, LMSelectDialogService, LMSelectResponse } from '@app/components';

enum LMLookupBasicDialogResult {
  CANCEL = 'CANCEL',
  OK = 'OK',
  TOOLBAREVT = 'TOOLBAREVT'
}

@Component({
  selector:    'lm-lookup-basic',
  templateUrl: './lm-lookup-basic.component.html'
})
export class LMLookupBasicComponent implements OnInit, AfterViewInit {
  @ViewChild(SohoLookupComponent) sohoLookup: SohoLookupComponent;
  @ViewChild('lookupBasicPlaceholder', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;
  @ViewChild(SohoBusyIndicatorDirective) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChild(SohoAlertDirective) sohoAlert: SohoAlertDirective;

  @Output() onLookupValueChangedEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  @Input() fieldLabel: string;
  @Input() sourceField: string;
  @Input() lookupValue: string;
  @Input() selectListUrl: string;

  @Input() viewType: 'form' | 'list';
  @Input() viewName: string;
  @Input() dataView: LMDataView;
  @Input() actionName: string;

  @Input() cubeBusClass: string;
  @Input() cubeDimension: LMDimension;
  @Input() cubeGetFieldsUrl: string;
  @Input() showAlert: boolean;
  @Input() topNodeBaseValue: string;
  private cubeSelectUIList: string;

  public id: string;
  private dialog: SohoModalDialogRef<any>;
  private lookupSelectionEvent: any;
  private lookupSelectionSubscription;
  public immutable = false;

  constructor(
    private ngZone: NgZone,
    private http: HttpClient,
    private element: ElementRef,
    private lmFormatUtilService: LMFormatUtilService,
    private lmLoggerService: LMLoggerService,
    private lmSpecService: LMSpecService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmContextService: LMContextService,
    private lmSelectDialogService: LMSelectDialogService,
    private sohoModalDialogService: SohoModalDialogService,
  ) {}

  ngOnInit(): void {
    if (this.cubeDimension) {
      this.fieldLabel = this.cubeDimension.label;
      this.sourceField = this.cubeDimension.id;
      this.lookupValue = this.cubeDimension.value;
      this.viewName = this.cubeDimension.name;
      this.cubeSelectUIList = this.cubeDimension.uilist;
      this.immutable = this.cubeDimension.immutable || false;
    }
    this.id = this.lmUniqueIdService.uniqueId([ 'lm-basic-lookup', this.sourceField ]);
  }

  ngAfterViewInit() {
    if (this.showAlert && this.cubeDimension.override && this.sohoAlert) {
      this.sohoAlert.addInlineMessage(Soho.Locale.translate('TopNodeValueChanged'), 'info', true, false);
    }
  }

  public onNgModelChange(value) {
    if (this.cubeDimension) {
      this.cubeDimension.value = this.lookupValue = value;
    }
  }

  public onValueChanged(event) {
    if (this.cubeDimension) {
      // this.cubeDimension.value = this.lookupValue = value;
      if (this.showAlert && this.sohoAlert) {
        this.cubeDimension.override = true;
        this.sohoAlert.removeAllMessages();
        this.sohoAlert.addInlineMessage(Soho.Locale.translate('TopNodeValueChanged'), 'info', true, false);
        this.onLookupValueChangedEvent.emit(new LMFieldValueChangedEvent(
          new CustomEvent('ValueChanged'), 'DisplayAlertEvent', this.sourceField));
      }
    } else if (this.viewName === 'CubeStructure') {
      // Context field or reporting basis
      this.onLookupValueChangedEvent.emit(new LMFieldValueChangedEvent(
        new CustomEvent('ValueChanged'), 'TriggerCubeContextValueChangeEvent', this.sourceField));
    } else {
      this.onLookupValueChangedEvent.emit(new LMFieldValueChangedEvent(
        new CustomEvent('ValueChanged'), 'ValueChangedEvent', this.sourceField));
    }
  }

  onLookupField = (dataView: LMDataView) =>
    // Process lookup selection.
    dataView.fields[ this.sourceField ].value
  ;

  onLookupClick = (e, lookup) => {
    if (!this.selectListUrl) {
      this.lmLoggerService.log([ 'LMLookupBasicComponent', 'The Select list URL required to perform a lookup is not set.' ]);
      return;
    }

    if (this.lmContextService.useSingleSelectList()) {
      this.showSelectListDialog();
    } else {
      this.showClassicSelectListDialog();
    }
  };

  private showSelectListDialog() {
    this.lmSpecService.getListSpec(this.selectListUrl).subscribe((listSpecResponse: LMListSpecResponse) => {
      this.lmLoggerService.log([ 'LMLookupBasicComponent', 'getListSpec() completed', listSpecResponse ]);

      const listUrlInfo = listSpecResponse.listUrlInfo;
      const listSpec = listSpecResponse.listSpec;
      const title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('SelectTitle'), this.fieldLabel);

      if (!this.dataView) {
        // The handcoded print to file or export to csv does not have a formSpec or fromDataview.
        // The concern here is that empty dataview may result in a backend failure.
        this.dataView = <LMDataView> { fields: {} };
        if (this.cubeDimension && this.topNodeBaseValue) {
          const keyNames = this.cubeDimension.relBusClassKeyNames.split('/');
          const keyValues = this.topNodeBaseValue.split('/');
          if (keyNames.length > 0) {
            keyNames.forEach((key, i) => { this.dataView.fields[key] = { value: keyValues[i] }; });
          }
        }
      }

      const selectRequestData: SelectRequestData = {
        viewType: (this.viewType) ? this.viewType : 'form',
        viewName: this.viewName,
        dataView: this.dataView,
        actionName: this.actionName,
        // Filter Dialog, child hierarchy dimension - Only top node and its children will be displayed
        displayTopNodeAndChildren: (this.cubeDimension && this.topNodeBaseValue !== undefined),
        // BusinessClassTotal, child hierarchy dimension - only top nodes will be displayed in a tree list
        useTopNodeFilter: (this.cubeDimension && this.cubeDimension.useTopNodeFilter !== undefined && this.cubeDimension.useTopNodeFilter === true)
      };

      // showAlert is set if the lookup is from Structure dialog. Only top nodes will be displayed in a simple list and not tree.
      if (this.showAlert) {
        selectRequestData.structureTopNodeFilter = true;
      }
      // In Structure, Filter or BusinessClassTotal dimension mapping dialogs, disable create mode.
      // Otherwise, BusinessObjectEvaluator will change the key field spec to that of the extended business class.
      // Lookup on FEG will return all FEGs and not EnterpriseGroups.
      if (this.cubeDimension) {
        selectRequestData.disableCreationMode = true;
      }

      this.lmSelectDialogService.showSelectListDialog({
        listUrlInfo,
        listSpec,
        title,
        sourceField: this.sourceField,
        selectRequestData
      }).pipe(
        first()) // causes the subscription to complete after its messaged the first time
        .subscribe((selectResponse: LMSelectResponse) => {
          this.lmLoggerService.log([ 'LMLookupBasicComponent', 'showSelectListDialog()', selectResponse ]);
          if (selectResponse?.status === AppConstants.STATUS_COMPLETED && selectResponse.dataViews) {
            const selectedDataView: LMDataView = selectResponse.dataViews[0];
            this.applyValue(selectedDataView).subscribe((response: any) => {
              if (response.status && response.status === AppConstants.STATUS_SYSTEM_ERROR) {
                this.lmErrorUtilService.handleError(response.message, response.details, response.status);
                return;
              }

              const resDataView: LMDataView = response;
              this.lookupValue = resDataView.fields[this.sourceField].value as string;
              if (this.cubeDimension) {
                this.cubeDimension.value = this.lookupValue;
              }

              // sohoLookupComponent requires a data property on each array element
              const selectedData: Array<any> = [ { data: resDataView } ] ;
              this.ngZone.runOutsideAngular(() => {
                this.sohoLookup.setValue(selectedData);
              });
            });
          } else if (selectResponse?.status === AppConstants.STATUS_CANCEL) {
            this.ngZone.runOutsideAngular(() => {
              this.element?.nativeElement?.querySelectorAll('input[soho-lookup]')[0]?.focus();
            });
          }
        }
        );
    },
    (error: any) => {
      this.lmLoggerService.log([ 'LMLookupBasicComponent error', error ]);
    });
  }

  // TODO: Remove the following and its supporting methods once we now longer support classic lookup selection
  private showClassicSelectListDialog() {
    this.busyIndicator.activated = true;

    const cancelButton = this.lmUniqueIdService.uniqueId([ 'lm-basic-lookup-', this.sourceField,  LMDialogButtonKey.CANCEL ]);
    const okButton = this.lmUniqueIdService.uniqueId([ 'lm-basic-lookup-', this.sourceField, LMDialogButtonKey.OK ]);

    const buttonIds = {
      ok: okButton,
      cancel: cancelButton
    };

    const buttons: SohoModalButton[] = [
      {
        id:    cancelButton,
        text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        // tslint:disable-next-line:no-shadowed-variable > shadowed variable e
        click: (e, modal) => {
          this.busyIndicator.activated = false;
          this.dialog.close(LMLookupBasicDialogResult.CANCEL);
        }
      },
      {
        id:        okButton,
        text:      Soho.Locale.translate(LMDialogButtonKey.OK),
        isDefault: true,
        // tslint:disable-next-line:no-shadowed-variable > shadowed variable e
        click:     (e, modal) => {
          // safety: need to be in the AngularZone
          this.ngZone.run(() => {
            this.handleSelection();
          });
        }
      }
    ];

    // this.lmSpecService.getListSpec(this.selectListUrl)
    // .subscribe((listSpec: LMListSpec) => {
    this.lmSpecService.getListSpec(this.selectListUrl).subscribe(
      (listSpecResponse: LMListSpecResponse) => {

        if ((listSpecResponse as LMSystemException)
        && ((listSpecResponse as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
         || (listSpecResponse as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
          // todo ppatton need to handle errors better
          return;
        }

        const listUrlInfo = listSpecResponse.listUrlInfo;
        const listSpec = listSpecResponse.listSpec;
        const title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('SelectTitle'), this.fieldLabel);

        this.dialog = this.sohoModalDialogService
          .modal<LMLookupDialogComponent>(LMLookupDialogComponent, this.placeholder, { fullsize: 'responsive', noRefocus: true })
          .buttons(buttons)
          .title(title)
          .isAlert(false)
          .autoFocus(false)
          .open()
          .apply((lookupDialog: LMLookupDialogComponent) => {
            this.ngZone.run(() => {
              this.doApply(lookupDialog, listUrlInfo, listSpec, buttonIds);
            });
          })
          .afterClose(result => {
            this.busyIndicator.activated = false;
            if (this.lookupSelectionSubscription) {
              this.lookupSelectionSubscription.unsubscribe();
              this.lookupSelectionSubscription = null;
            }

            this.lookupSelectionEvent = null;
            this.dialog = null;
          });
      },
      error => {
        // todo ppatton need to handle errors better
      });
  };

  private doApply(lookupDialog: LMLookupDialogComponent, listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, buttonIds: {ok: string, cancel: string}) {
    const viewType = (this.viewType) ? this.viewType : 'form';
    if (!this.dataView) {
      // The handcoded example print to file or export to csv does not have a formSpec or fromDataview.
      // The concern here is that empty dataview may result in a backend failure.
      this.dataView = <LMDataView> { fields: {} };
      if (this.cubeDimension && this.topNodeBaseValue) {
        const keyNames = this.cubeDimension.relBusClassKeyNames.split('/');
        const keyValues = this.topNodeBaseValue.split('/');
        if (keyNames.length > 0) {
          keyNames.forEach((key, i) => { this.dataView.fields[key] = { value: keyValues[i] }; });
        }
        // this.dataView.fields[this.cubeDimension.selectField] = {value: this.topNodeBaseValue};
      }
    }

    lookupDialog.listUrlInfo = listUrlInfo;
    lookupDialog.listSpec = listSpec;
    lookupDialog.buttonIds = buttonIds;

    lookupDialog.selectRequestData = {
      viewType,
      viewName: this.viewName,
      dataView: this.dataView,
      actionName: this.actionName,
      displayTopNodeAndChildren: (this.cubeDimension && this.topNodeBaseValue !== undefined),
      useTopNodeFilter: (this.cubeDimension && this.cubeDimension.useTopNodeFilter !== undefined && this.cubeDimension.useTopNodeFilter === true)
    };

    lookupDialog.titleChanged.pipe(first()).subscribe(newTitle => {
      this.dialog.title(newTitle);
    });

    // need to listen for all occurrences as will get a row selection for a 'deselection'
    this.lookupSelectionSubscription =
      lookupDialog.lookupSelection.subscribe((event: any) => {
        this.lookupSelectionEvent = event;
      });

    lookupDialog.navigatingToolbarEvent.pipe(first()).subscribe(() => {
      this.dialog.close(LMLookupBasicDialogResult.TOOLBAREVT);
    });

    lookupDialog.triggerDetectChanges();
  }

  private handleSelection() {
    if (this.lookupSelectionEvent) {
      const result: Array<any> = this.lookupSelectionEvent.rows;
      if (result && Array.isArray(result) && result.length > 0) {
        const dataView: LMDataView = result[ 0 ].data;
        this.applyValue(dataView).subscribe((response: any) => {
          if (response.status && response.status === AppConstants.STATUS_SYSTEM_ERROR) {
            this.lmErrorUtilService.handleError(response.message, response.details, response.status);
            return;
          }
          const resDataView: LMDataView = response;
          this.lookupValue = resDataView.fields[ this.sourceField ].value as string;
          if (this.cubeDimension) {
            this.cubeDimension.value = this.lookupValue;
          }
          result[0].data = resDataView;
          this.ngZone.runOutsideAngular(() => {
            this.sohoLookup.setValue(result);
          });
        });
      }
    }
    this.dialog.close(LMLookupBasicDialogResult.OK);
  }

  private applyValue(dataView: LMDataView): Observable<any> {
    // Cube Cell relation requires the context values in ontological context to be prefixed to the selected value.
    // getFields call will return the ontological context values along with the selected key.
    if (this.cubeGetFieldsUrl !== undefined) {
      const cubeCellRequest = Object.assign(
        { requestType: FormRequestType.CubeRelGetSelectValue },
        { busClass: this.cubeBusClass },
        { dimension: this.sourceField },
        { selectResourceId: dataView.resourceId + '.' + this.cubeSelectUIList },
        { selectDataView: dataView },
      );
      return this.http
        .post(this.cubeGetFieldsUrl, cubeCellRequest).pipe(
          catchError(error => {
            this.lmLoggerService.error(error);
            return throwError(error || 'Server error');
          }));
    } else {
      return of(dataView);
    }
  }
}
